import { useEffect, useRef, useState } from 'react'
import { ChevronDown, Download, FileSpreadsheet, FileText, Settings2, X } from 'lucide-react'
import {
  adminApi,
  type ExportColumnOption,
  type ExportFormat,
  type SessionExportPayload,
} from '../lib/api/admin'

/**
 * Format picker + options panel for a data export.
 *
 * Deliberately knows nothing about sessions: the parent supplies the current
 * filters via `buildPayload`, so the same component can drive an export on any
 * admin list page. Column options are fetched from the backend registry rather
 * than hardcoded here, so the UI can never offer a column the renderers do not
 * know about.
 */

export interface ExportMenuProps {
  /** Returns the filters for the export. Called at click time, so it always
   *  reflects the parent's current tab/search/interviewer state. */
  buildPayload: (format: ExportFormat, options: ExportOptions) => SessionExportPayload
  onExport: (payload: SessionExportPayload) => void | Promise<void>
  /** Timezone pre-selected in the options panel (usually the platform default). */
  defaultTimezone?: string
  busy?: boolean
  disabled?: boolean
  label?: string
}

export interface ExportOptions {
  timezone: string
  /** `undefined` = use the backend's default column set. */
  columns?: string[]
}

const FORMATS: {
  format: ExportFormat
  label: string
  hint: string
  Icon: typeof FileText
}[] = [
  {
    format: 'xlsx',
    label: 'Excel (.xlsx)',
    hint: 'Best for filtering and pivoting',
    Icon: FileSpreadsheet,
  },
  {
    format: 'csv',
    label: 'CSV (.csv)',
    hint: 'Smallest file, opens anywhere',
    Icon: FileText,
  },
  {
    format: 'pdf',
    label: 'PDF (.pdf)',
    hint: 'Printable report, up to 5,000 rows',
    Icon: FileText,
  },
]

/** Timezones offered in the picker, plus whatever the platform default is. */
const COMMON_TIMEZONES = [
  'UTC',
  'America/New_York',
  'America/Chicago',
  'America/Los_Angeles',
  'Europe/London',
  'Europe/Paris',
  'Asia/Kolkata',
  'Asia/Dubai',
  'Asia/Singapore',
  'Australia/Sydney',
]

export default function ExportMenu({
  buildPayload,
  onExport,
  defaultTimezone = 'UTC',
  busy = false,
  disabled = false,
  label = 'Export',
}: ExportMenuProps) {
  const [open, setOpen] = useState(false)
  const [showOptions, setShowOptions] = useState(false)
  const [timezone, setTimezone] = useState(defaultTimezone)
  const [columnOptions, setColumnOptions] = useState<ExportColumnOption[]>([])
  const [selected, setSelected] = useState<Set<string> | null>(null)
  const wrapRef = useRef<HTMLDivElement>(null)

  // Follow the platform timezone until the admin overrides it here.
  useEffect(() => setTimezone(defaultTimezone), [defaultTimezone])

  // Close on outside click / Escape — expected behaviour for a dropdown.
  useEffect(() => {
    if (!open) return
    const onClick = (e: MouseEvent) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target as Node)) setOpen(false)
    }
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false)
    }
    document.addEventListener('mousedown', onClick)
    document.addEventListener('keydown', onKey)
    return () => {
      document.removeEventListener('mousedown', onClick)
      document.removeEventListener('keydown', onKey)
    }
  }, [open])

  // Fetched lazily: most admins never open the options panel.
  const loadColumns = async () => {
    if (columnOptions.length) return
    try {
      const options = await adminApi.getExportColumns()
      setColumnOptions(options)
      setSelected(new Set(options.filter((o) => o.default).map((o) => o.key)))
    } catch {
      // Non-fatal: without the catalogue the export just uses backend defaults.
    }
  }

  const currentOptions = (): ExportOptions => ({
    timezone,
    // Send nothing when the selection matches the defaults, so the backend
    // stays the source of truth for "the usual columns".
    columns:
      selected && columnOptions.length && !isDefaultSelection(selected, columnOptions)
        ? columnOptions.filter((o) => selected.has(o.key)).map((o) => o.key)
        : undefined,
  })

  const pick = async (format: ExportFormat) => {
    setOpen(false)
    await onExport(buildPayload(format, currentOptions()))
  }

  const toggleColumn = (key: string) => {
    setSelected((prev) => {
      const next = new Set(prev ?? [])
      if (next.has(key)) next.delete(key)
      else next.add(key)
      return next
    })
  }

  const noColumnsSelected = selected !== null && selected.size === 0

  return (
    <div ref={wrapRef} style={{ position: 'relative', display: 'inline-block' }}>
      <button
        type="button"
        className="btn btn-secondary as-refresh-btn"
        onClick={() => setOpen((v) => !v)}
        disabled={disabled || busy}
        aria-haspopup="menu"
        aria-expanded={open}
      >
        <Download size={18} />
        <span className="as-refresh-label">{busy ? 'Exporting…' : label}</span>
        <ChevronDown size={14} />
      </button>

      {open && (
        <div
          role="menu"
          style={{
            position: 'absolute',
            right: 0,
            top: 'calc(100% + 6px)',
            zIndex: 40,
            minWidth: 260,
            background: 'var(--surface, #fff)',
            border: '1px solid var(--border, #e5e7eb)',
            borderRadius: 10,
            boxShadow: '0 12px 32px rgba(0,0,0,.12)',
            overflow: 'hidden',
          }}
        >
          <div
            style={{
              padding: '8px 12px',
              fontSize: 11,
              fontWeight: 600,
              letterSpacing: '.04em',
              textTransform: 'uppercase',
              color: 'var(--text-muted)',
            }}
          >
            Download current view as
          </div>

          {FORMATS.map(({ format, label: formatLabel, hint, Icon }) => (
            <button
              key={format}
              type="button"
              role="menuitem"
              onClick={() => pick(format)}
              disabled={noColumnsSelected}
              style={{
                display: 'flex',
                alignItems: 'flex-start',
                gap: 10,
                width: '100%',
                padding: '9px 12px',
                background: 'none',
                border: 'none',
                textAlign: 'left',
                cursor: noColumnsSelected ? 'not-allowed' : 'pointer',
                opacity: noColumnsSelected ? 0.5 : 1,
              }}
            >
              <Icon size={16} style={{ marginTop: 2, flexShrink: 0 }} />
              <span>
                <span style={{ display: 'block', fontSize: 13, fontWeight: 500 }}>
                  {formatLabel}
                </span>
                <span style={{ display: 'block', fontSize: 11, color: 'var(--text-muted)' }}>
                  {hint}
                </span>
              </span>
            </button>
          ))}

          <div style={{ borderTop: '1px solid var(--border, #e5e7eb)' }}>
            <button
              type="button"
              onClick={() => {
                setOpen(false)
                setShowOptions(true)
                loadColumns()
              }}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 8,
                width: '100%',
                padding: '9px 12px',
                background: 'none',
                border: 'none',
                cursor: 'pointer',
                fontSize: 12.5,
                color: 'var(--text-muted)',
              }}
            >
              <Settings2 size={14} />
              Export options
              <span style={{ marginLeft: 'auto', fontSize: 11 }}>{timezone}</span>
            </button>
          </div>

          {noColumnsSelected && (
            <div
              style={{
                padding: '6px 12px 10px',
                fontSize: 11,
                color: 'var(--danger, #dc2626)',
              }}
            >
              Select at least one column in Export options.
            </div>
          )}
        </div>
      )}

      {showOptions && (
        <div className="modal-overlay" onClick={() => setShowOptions(false)}>
          <div
            className="modal"
            onClick={(e) => e.stopPropagation()}
            style={{ maxWidth: 520 }}
          >
            <div className="modal-header">
              <h3 className="modal-title">Export Options</h3>
              <button
                onClick={() => setShowOptions(false)}
                style={{ background: 'none', border: 'none', cursor: 'pointer' }}
              >
                <X size={20} />
              </button>
            </div>

            <div className="modal-body">
              <div className="form-group">
                <label className="form-label">Display timezone</label>
                <select
                  className="form-input"
                  value={timezone}
                  onChange={(e) => setTimezone(e.target.value)}
                >
                  {Array.from(new Set([defaultTimezone, ...COMMON_TIMEZONES])).map((tz) => (
                    <option key={tz} value={tz}>
                      {tz}
                      {tz === defaultTimezone ? ' (platform default)' : ''}
                    </option>
                  ))}
                </select>
                <p style={{ fontSize: 11.5, color: 'var(--text-muted)', marginTop: 6 }}>
                  Applies to the local date/time columns. A UTC column is always
                  included as well, so a file is never ambiguous.
                </p>
              </div>

              <div className="form-group" style={{ marginTop: 16 }}>
                <label className="form-label">Columns</label>
                {columnOptions.length === 0 ? (
                  <p style={{ fontSize: 12.5, color: 'var(--text-muted)' }}>
                    Loading columns…
                  </p>
                ) : (
                  <div
                    style={{
                      display: 'grid',
                      gridTemplateColumns: 'repeat(auto-fill, minmax(190px, 1fr))',
                      gap: 6,
                      maxHeight: 240,
                      overflowY: 'auto',
                    }}
                  >
                    {columnOptions.map((option) => (
                      <label
                        key={option.key}
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: 7,
                          fontSize: 12.5,
                          cursor: 'pointer',
                        }}
                      >
                        <input
                          type="checkbox"
                          checked={selected?.has(option.key) ?? false}
                          onChange={() => toggleColumn(option.key)}
                        />
                        {option.header}
                      </label>
                    ))}
                  </div>
                )}
              </div>
            </div>

            <div className="modal-footer">
              <button
                type="button"
                className="btn btn-secondary"
                onClick={() => {
                  setTimezone(defaultTimezone)
                  setSelected(
                    new Set(columnOptions.filter((o) => o.default).map((o) => o.key)),
                  )
                }}
              >
                Reset to defaults
              </button>
              <button
                type="button"
                className="btn btn-primary"
                onClick={() => setShowOptions(false)}
              >
                Done
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

function isDefaultSelection(
  selected: Set<string>,
  options: ExportColumnOption[],
): boolean {
  const defaults = options.filter((o) => o.default).map((o) => o.key)
  return (
    selected.size === defaults.length && defaults.every((key) => selected.has(key))
  )
}
